import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { h } from '@stencil/core';

import { Consent } from '../forms-ui-form-faas.interface';
import { FormSubmissionConsent } from '../forms-ui-form-payload.interface';

const DATE_FORMAT = 'YYYY-MM-DDThh:mm:ss:SSS000';
dayjs.extend(utc);

export interface IConsentService {
  renderAndManageConsents(consents: Consent[]): any
  prepareConsentsForSubmission(): FormSubmissionConsent[]
  validateConsents(): boolean
}

export class ConsentService implements IConsentService {
	private consents: Consent[];

	private consentRefs: HTMLFormsUiCheckboxElement[];

	/**
	 * Saves provided consensts for management by this service. Renders the
	 * provided consents
	 * @param consents - Array of consents to render and manage
	 * @returns - An HTML Div containing all rendered consents
	 */
	renderAndManageConsents(consents: Consent[]): HTMLDivElement {
		this.consents = consents;

		// Clear out consent refs so we always have up to date refs
		this.consentRefs = [];

		return <div class="forms-ui-consents">
			{this.consents.map((consent, index) => this.renderConsent(consent, index))}
		</div>;
	}

	/**
	 * Internal function for rendering a single consent
	 * @param consent - consent to render
	 * @param index - Index of consent in consent list. Used for unique name
	 * @returns - An HTML Div containing a rendered consent.
	 */
	renderConsent(consent: Consent, index: number): HTMLDivElement {
		if (consent.implicit) {
			return <div
				class="forms-ui-implicit-consent"
				innerHTML={consent.consentUseData[0].consentData.data}>
			</div>;
		}
		const { required } = consent;
		const label = consent.consentUseData[0].consentData.data;
		const name = `consent-${index}-form`;

		return <div class="forms-ui-explicit-consent">
			<forms-ui-checkbox
				name={name}
				value={consent.uuid}
				label={label}
				required={required}
				onFormsUICheckboxUpdate={/* istanbul ignore next */ (e) => this.handleConsentUpdate(e)}
				ref={(ref) => this.consentRefs.push(ref)}/>
		</div>;
	}

	/**
	 * Applies the state of a checkbox to its corresponding
	 * consent state
	 */
	handleConsentUpdate(e: any) {
		const uuid = e.target.value;
		const status = e.target.checked;
		this.consents.find((_consent) => _consent.uuid === uuid).accepted = status;
	}

	/**
	 * Builds a consent payload for form submission. Will contain all implicit consents
	 * and all accepted explicit consents
	 * @returns - Array of consent objects for form submission
	 */
	prepareConsentsForSubmission(): FormSubmissionConsent[] {
		const returnConsents: FormSubmissionConsent[] = [];

		/**
		 * Explicit consents will use the accepted bit to denote whether or not the user has accepted
		 * the consent or not. Implicit consents are always accepted if the user submits the form
		 */
		const acceptedRawConsents = this.consents.filter((consent) => consent.accepted || consent.implicit);

		acceptedRawConsents.forEach((consent) => {
			// we've already filtered out non-accepted consents
			const agreed = true;
			const consentDate = `${dayjs().utc(true).format(DATE_FORMAT)}Z`;
			const lastModifiedDate = `${dayjs(Number(consent.date)).utc(true).format(DATE_FORMAT)}Z`;

			const {
				required,
				consentUrl,
				serviceCode,
				serviceFunctionCode,
				serviceName,
				locale,
				currentVersion,
				zoomInfoConsent,
				uuid,
			} = consent;

			const consentType = consent.consentUseData[0].consentData.documenttype;
			const { useCategoryCode } = consent.consentUseData[0].useCategory;
			const { useCategoryValue } = consent.consentUseData[0].useCategory;
			const useCategoryDesc = consent.consentUseData[0].useCategory.userCategoryDiscription;

			returnConsents.push({
				uuid,
				required,
				consentUrl,
				serviceCode,
				serviceFunctionCode,
				serviceName,
				locale,
				currentVersion,
				zoomInfoConsent,
				agreed,
				consentType,
				lastModifiedDate,
				consentDate,
				useCategoryCode,
				useCategoryValue,
				useCategoryDesc,
			});
		});

		return returnConsents;
	}

	/**
	 * Calls validate on all explicit consent checkboxes and checks that all required consents are
	 * agreed to by the user.
	 * @returns - True is all required, explicit consents are agreed to by the user. False otherwise
	 */
	validateConsents(): boolean {
		this.consentRefs.forEach((cr) => {
			cr.validate();
		});

		return !this.consents.some((consent) => !consent.implicit && consent.required && !consent.accepted);
	}
}

/**
 * Creates a ConsentService
 * @returns IConsentService
 */
export const createConsentService = (): IConsentService => new ConsentService();
